import Icon from '@/components/icons/Icon';
import { IconVariant } from '@/components/icons/types';
import React from 'react';
import { twMerge } from 'tailwind-merge';
import { classnames } from '@/helpers';

type ChipItemProps<T extends React.ElementType = 'button'> = {
  as?: T;
  label: string;
  icon?: IconVariant;
  /** Custom leading element (e.g. an Avatar/Symbol) shown instead of `icon`. */
  avatar?: React.ReactNode;
  selected?: boolean;
  selectedIcon?: IconVariant;
  withSelectedIcon?: boolean;
  className?: string;
  textClassName?: string;
} & Omit<
  React.ComponentPropsWithoutRef<T>,
  keyof {
    as?: T;
    label: string;
    icon?: IconVariant;
    avatar?: React.ReactNode;
    selected?: boolean;
    selectedIcon?: IconVariant;
    className?: string;
  }
>;

const ChipItem = <T extends React.ElementType = 'button'>({
  as,
  label,
  icon,
  avatar,
  selected = false,
  selectedIcon,
  withSelectedIcon = true,
  onClick,
  className,
  textClassName,
  ...rest
}: ChipItemProps<T>) => {
  const Component = as || 'button';

  return (
    <Component
      onClick={onClick}
      className={twMerge(
        classnames(
          'gap-2xs pl-sm pr-sm py-xs focus-visible:outline-border_focus inline-flex items-center rounded-full outline-none transition-all focus-visible:outline focus-visible:outline-4',
          'border-border_bold bg-bg_neutral text-fg_secondary border',
          {
            selected: selected,
            'hover:bg-bg_secondary_bold': !selected,
            'bg-bg_primary text-fg_primary_inverse border-bg_primary': selected,
            'pl-2xs': !!avatar,
            [className]: !!className,
          },
        ),
      )}
      style={{ maxHeight: 36 }}
      aria-pressed={Component === 'button' ? selected : undefined}
      {...rest}>
      {avatar}
      {!avatar && icon && (
        <Icon
          variant={icon}
          style={{ width: 20, height: 20 }}
          color={selected ? 'fg_primary_inverse' : 'fg_secondary'}
        />
      )}
      <span className={classnames('whitespace-nowrap text-sm', { [textClassName!]: !!textClassName })}>{label}</span>
      {withSelectedIcon && (
        <Icon
          variant={selectedIcon ?? 'check'}
          color="fg_primary_inverse"
          style={{ width: 20, height: 20 }}
          className={classnames({ invisible: !selected })}
        />
      )}
    </Component>
  );
};

export default ChipItem;
